/**
 * Centre-focus carousel for the Explore by Features section.
 *
 * The track is translated so the active slide sits in the middle of the
 * viewport. It loops endlessly: `PAD` clones of the last slides go before the
 * first and clones of the first slides after the last, so both neighbours of
 * the centre always exist. Moving past an end rides onto a clone, then jumps
 * — transition off — to the real slide it copies. Same technique as the hero.
 *
 * Clicking a side card centres it; only the centre card's link navigates.
 * Arrows, drag and swipe move one slide. No autoplay.
 *
 * Shared by Explore by Features and Testimonials. Two optional extras, both
 * opted into from the markup:
 *   [data-fc-dots]           one dot per real slide, following the centre
 *                            through the loop. `data-dot-class` names the dot
 *                            class, `data-dot-label` its aria-label ([n]).
 *   data-fc-pause-inactive   on the element: pause any video in a slide that
 *                            leaves the centre (filmed testimonials).
 *
 * Guarded, because a section can appear twice and every `<script src>`
 * executes on each occurrence.
 */
(() => {
  if (customElements.get('feature-carousel')) return;

  // Enough to fill a bled carousel on an ultrawide screen: ~2.5 slots show
  // each side of the centre, and riding onto a clone at either end uses one.
  const PAD = 4;
  const SWIPE = 45;

  class FeatureCarousel extends HTMLElement {
    connectedCallback() {
      if (this.started) return;

      this.viewport = this.querySelector('[data-fc-viewport]');
      this.track = this.querySelector('[data-fc-track]');
      this.prevButton = this.querySelector('[data-fc-prev]');
      this.nextButton = this.querySelector('[data-fc-next]');
      if (!this.viewport || !this.track) return;

      this.slides = Array.from(this.track.querySelectorAll(':scope > [data-fc-slide]'));
      this.total = this.slides.length;
      if (this.total < 2) return;

      this.started = true;
      this.position = 0;

      // Inside a clipped track the page-load scripts would hold these at
      // opacity 0: `reveal` waits for an intersection a hidden slide never
      // has, and a lazy image may not load until it slides in. Both are
      // settled here once, for originals and clones alike.
      for (const el of this.track.querySelectorAll('.reveal')) el.classList.add('is-visible');
      for (const img of this.track.querySelectorAll('img[loading="lazy"]')) img.loading = 'eager';

      const pad = Math.min(PAD, this.total);
      this.pad = pad;
      const before = this.slides.slice(-pad).map((slide) => this.cloneSlide(slide));
      const after = this.slides.slice(0, pad).map((slide) => this.cloneSlide(slide));
      this.track.prepend(...before);
      this.track.append(...after);
      this.all = Array.from(this.track.children);

      this.classList.add('fc--ready');

      this.all.forEach((slide, i) => {
        slide.addEventListener('click', (event) => this.onSlideClick(event, i));
      });

      if (this.prevButton && this.nextButton) {
        this.prevButton.hidden = false;
        this.nextButton.hidden = false;
        this.prevButton.addEventListener('click', () => this.go(this.position - 1));
        this.nextButton.addEventListener('click', () => this.go(this.position + 1));
      }

      this.buildDots();

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') this.go(this.position - 1);
        if (event.key === 'ArrowRight') this.go(this.position + 1);
      });

      // Tabbing onto a side card centres it. The browser also scrolls the
      // clipped viewport to show the focused element, which would throw every
      // offset off, so that scroll is undone.
      this.track.addEventListener('focusin', (event) => {
        this.viewport.scrollLeft = 0;
        const slide = event.target.closest('[data-fc-slide]');
        const i = this.all.indexOf(slide);
        if (i !== -1 && i !== this.position + this.pad) this.go(i - this.pad);
      });

      this.track.addEventListener('transitionend', (event) => {
        if (event.target === this.track && event.propertyName === 'transform') this.settle();
      });

      this.bindDrag();

      new ResizeObserver(() => this.place(false)).observe(this.viewport);
      this.place(false);
    }

    cloneSlide(slide) {
      const clone = slide.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.fcClone = '';
      for (const el of clone.querySelectorAll('a, button, [tabindex]')) el.tabIndex = -1;
      for (const img of clone.querySelectorAll('img')) {
        img.loading = 'eager';
        img.classList.add('is-loaded');
      }
      return clone;
    }

    /** Distance the track must move so slide `i` (of `this.all`) is centred. */
    offsetFor(i) {
      const slide = this.all[i];
      return slide.offsetLeft + slide.offsetWidth / 2 - this.viewport.clientWidth / 2;
    }

    translate(px, animate) {
      if (!animate) this.track.style.transition = 'none';
      this.track.style.transform = `translateX(${-px}px)`;
      if (!animate) {
        void this.track.offsetWidth;
        this.track.style.transition = '';
      }
    }

    place(animate) {
      const i = this.position + this.pad;
      this.all.forEach((slide, n) => slide.classList.toggle('is-active', n === i));
      this.translate(this.offsetFor(i), animate);
      this.syncDots();

      if (this.hasAttribute('data-fc-pause-inactive')) {
        this.all.forEach((slide, n) => {
          if (n === i) return;
          for (const video of slide.querySelectorAll('video')) {
            if (!video.paused) video.pause();
          }
        });
      }
    }

    buildDots() {
      this.dotsWrap = this.querySelector('[data-fc-dots]');
      if (!this.dotsWrap) return;

      const className = this.dotsWrap.dataset.dotClass || 'fc-dot';
      const label = this.dotsWrap.dataset.dotLabel || 'Go to slide [n]';

      this.dots = this.slides.map((_, n) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = className;
        dot.setAttribute('aria-label', label.replace('[n]', n + 1));
        dot.addEventListener('click', () => this.go(n));
        return dot;
      });
      this.dotsWrap.replaceChildren(...this.dots);
    }

    /** The dot of the real slide, even while the track rides on a clone. */
    syncDots() {
      if (!this.dots) return;
      const real = ((this.position % this.total) + this.total) % this.total;
      this.dots.forEach((dot, n) => {
        dot.classList.toggle('is-active', n === real);
        if (n === real) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    go(target) {
      this.position = Math.max(-this.pad, Math.min(this.total - 1 + this.pad, target));
      const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.place(!still);
      // `transitionend` never fires under reduced motion, or if the transform
      // did not change, so the loop is also settled on a timer.
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(() => this.settle(), still ? 0 : 700);
    }

    /** After riding onto a clone, jump silently to the real slide it copies. */
    settle() {
      clearTimeout(this.settleTimer);
      const real = ((this.position % this.total) + this.total) % this.total;
      if (real === this.position) return;
      this.position = real;
      this.classList.add('fc--jumping');
      this.place(false);
      void this.track.offsetWidth;
      this.classList.remove('fc--jumping');
    }

    onSlideClick(event, i) {
      if (this.suppressClick) {
        event.preventDefault();
        return;
      }
      if (i !== this.position + this.pad) {
        event.preventDefault();
        this.go(i - this.pad);
      }
    }

    bindDrag() {
      let startX = 0;
      let startY = 0;
      let lastX = 0;
      let axis = null;
      let down = false;
      let base = 0;

      const end = (cancelled) => {
        if (!down) return;
        down = false;
        this.classList.remove('is-dragging');
        if (axis !== 'x') return;

        // The last pointermove, not the up event: pointerup/pointercancel can
        // report a stale or zero clientX on touch, which sent every swipe the
        // same way in the hero.
        const dx = lastX - startX;
        this.suppressClick = true;
        setTimeout(() => (this.suppressClick = false), 0);

        if (!cancelled && Math.abs(dx) > SWIPE) {
          this.go(this.position + (dx < 0 ? 1 : -1));
        } else {
          this.place(true);
        }
      };

      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.button !== 0) return;
        down = true;
        axis = null;
        startX = lastX = event.clientX;
        startY = event.clientY;
        base = this.offsetFor(this.position + this.pad);
      });

      this.viewport.addEventListener('pointermove', (event) => {
        if (!down) return;
        lastX = event.clientX;
        const dx = lastX - startX;
        const dy = event.clientY - startY;

        if (!axis) {
          if (Math.abs(dx) + Math.abs(dy) < 6) return;
          axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (axis === 'x') {
            this.classList.add('is-dragging');
            this.viewport.setPointerCapture?.(event.pointerId);
          }
        }

        if (axis === 'x') this.translate(base - dx, false);
      });

      this.viewport.addEventListener('pointerup', () => end(false));
      this.viewport.addEventListener('pointercancel', () => end(true));
    }
  }

  customElements.define('feature-carousel', FeatureCarousel);
})();
